/**
 * Planning — Module de planification centralisée.
 * L'administrateur crée un planning pour les commerciaux / techniciens / employés.
 * Vues : Jour / Semaine / Mois.
 * Filtres : rôle (Tous / Commerciaux / Techniciens / Employés), employé, type d'activité.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, Edit3, X,
  Users, Wrench, Briefcase, Filter, ClipboardList, AlertCircle, Sparkles, Loader2
} from 'lucide-react';

const ROLE_TABS = [
  { key: 'tous',         label: 'Tous',         icon: Users,      color: 'slate' },
  { key: 'commercial',   label: 'Commerciaux',  icon: Briefcase,  color: 'blue'  },
  { key: 'technicien',   label: 'Techniciens',  icon: Wrench,     color: 'orange'},
  { key: 'employe',      label: 'Employés',     icon: ClipboardList,color:'emerald'},
];

const VIEW_TABS = [
  { key: 'day',   label: 'Jour' },
  { key: 'week',  label: 'Semaine' },
  { key: 'month', label: 'Mois' },
];

const HOURS = Array.from({ length: 11 }, (_, i) => 8 + i); // 08:00 → 18:00

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const dayLabel = (d) => d.toLocaleDateString('fr-FR', {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
});

const short = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' });

const addDays = (d, n) => {
  const c = new Date(d); c.setDate(c.getDate() + n); return c;
};

const startOfWeek = (d) => {
  const c = new Date(d);
  const day = (c.getDay() + 6) % 7; // Lundi = 0
  c.setDate(c.getDate() - day);
  return c;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const roleBadgeClass = (role) => {
  const map = {
    commercial: 'bg-blue-100 text-blue-700 border-blue-200',
    technicien: 'bg-orange-100 text-orange-700 border-orange-200',
    employe: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    admin: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return map[role] || 'bg-slate-100 text-slate-700 border-slate-200';
};

export default function Planning({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('day');
  const [roleTab, setRoleTab] = useState('tous');
  const [current, setCurrent] = useState(new Date());
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showAuto, setShowAuto] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAdmin = user?.role === 'admin';

  // ─────────── Fetching ───────────
  const fetchTypes = useCallback(async () => {
    try {
      const r = await axios.get('/planning/activity-types');
      setTypes(r.data || []);
    } catch { /* no-op */ }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get('/planning/users');
      setUsers(r.data || []);
    } catch { /* no-op */ }
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      if (view === 'day') {
        params.date = fmt(current);
      } else if (view === 'week') {
        const start = startOfWeek(current);
        params.date_from = fmt(start);
        params.date_to = fmt(addDays(start, 6));
      } else {
        params.date_from = fmt(startOfMonth(current));
        params.date_to = fmt(endOfMonth(current));
      }
      if (roleTab !== 'tous') params.role = roleTab;
      if (filterUser !== 'all') params.user_id = filterUser;
      const r = await axios.get('/planning', { params });
      setActivities(r.data || []);
    } catch (e) {
      toast.error('Erreur chargement planning');
    } finally { setLoading(false); }
  }, [view, current, roleTab, filterUser]);

  useEffect(() => { fetchTypes(); fetchUsers(); }, [fetchTypes, fetchUsers]);
  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  // ─────────── Navigation date ───────────
  const goPrev = () => {
    if (view === 'day') setCurrent(addDays(current, -1));
    else if (view === 'week') setCurrent(addDays(current, -7));
    else setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  };
  const goNext = () => {
    if (view === 'day') setCurrent(addDays(current, 1));
    else if (view === 'week') setCurrent(addDays(current, 7));
    else setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  };
  const goToday = () => setCurrent(new Date());

  // ─────────── CRUD ───────────
  const deleteActivity = async (id) => {
    if (!window.confirm('Supprimer cette activité ?')) return;
    try {
      await axios.delete(`/planning/${id}`);
      toast.success('Activité supprimée');
      fetchActivities();
    } catch { toast.error("Erreur suppression"); }
  };

  const filteredUsers = useMemo(() => {
    if (roleTab === 'tous') return users;
    return users.filter(u => u.role === roleTab);
  }, [users, roleTab]);

  // ─────────── Stats ───────────
  const stats = useMemo(() => {
    const acc = { rendez_vous: 0, relance: 0, prospection: 0, intervention: 0, litige: 0, tache: 0, total: activities.length };
    activities.forEach(a => {
      if (acc[a.type] !== undefined) acc[a.type]++;
      else if (['mise_a_jour','reparation','changement_toile','maintenance'].includes(a.type)) acc.intervention++;
    });
    return acc;
  }, [activities]);

  const headerTitle = view === 'day'
    ? dayLabel(current)
    : view === 'week'
      ? `Semaine du ${short(startOfWeek(current))} au ${short(addDays(startOfWeek(current), 6))}`
      : current.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const chips = [
    ['Rendez-vous', stats.rendez_vous, '#2563eb'], ['Relances', stats.relance, '#16a34a'],
    ['Prospection', stats.prospection, '#ea8a1a'], ['Interventions', stats.intervention, '#0891b2'],
    ['Litiges', stats.litige, '#dc2626'], ['Tâches', stats.tache, '#7c3aed'],
  ].filter(([l, n]) => n > 0 || l !== 'Litiges');

  return (
    <Layout user={user} onLogout={onLogout} currentPage="planning">
      <div className="space-y-4" data-testid="planning-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900">Planning</h1>
            <p className="text-slate-500">Qui fait quoi, et à quelle heure, dans vos équipes</p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAuto(true)} className="h-11 px-5 font-bold text-blue-700 border-slate-200 bg-white" data-testid="pl-auto-btn">
                Planning automatique
              </Button>
              <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700 h-11 px-5 font-bold gap-1" data-testid="pl-new-btn">
                <Plus className="h-4 w-4" /> Créer une activité
              </Button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={goPrev} data-testid="pl-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-9" onClick={goToday} data-testid="pl-today">Aujourd'hui</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={goNext} data-testid="pl-next"><ChevronRight className="h-4 w-4" /></Button>
          <div className="text-lg font-bold text-slate-900 ml-2 first-letter:uppercase" data-testid="pl-title">{headerTitle}</div>
          <div className="flex-1" />
          {isAdmin && (
            <div className="flex bg-slate-100 rounded-xl p-1">
              {ROLE_TABS.map(r => (
                <button key={r.key} onClick={() => setRoleTab(r.key)} data-testid={`pl-role-${r.key}`}
                  className={`px-4 py-1.5 text-[15px] rounded-lg transition-colors ${roleTab === r.key ? 'bg-blue-600 text-white font-bold shadow' : 'text-slate-600 hover:text-slate-900'}`}>
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {isAdmin && (
            <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} className="h-9 text-sm border border-slate-200 rounded-lg px-2 bg-white" data-testid="pl-filter-user">
              <option value="all">Toute l'équipe</option>
              {filteredUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <div className="flex bg-slate-100 rounded-xl p-1">
            {VIEW_TABS.map(v => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`pl-view-${v.key}`}
                className={`px-4 py-1.5 text-[15px] rounded-lg transition-colors ${view === v.key ? 'bg-blue-600 text-white font-bold shadow' : 'text-slate-600 hover:text-slate-900'}`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap" data-testid="pl-stats">
          <span className="text-lg font-bold text-slate-900 mr-1">{stats.total} activité{stats.total > 1 ? 's' : ''}</span>
          {chips.map(([l, n, c]) => (
            <span key={l} className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-[15px] text-slate-700" data-testid={`pl-chip-${l}`}>
              <span className="w-3 h-3 rounded-sm" style={{ background: c }} />{l} <b className="text-slate-900">{n}</b>
            </span>
          ))}
        </div>

        {!isAdmin && (
          <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2 inline-block">
            Vous consultez uniquement votre propre planning.
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {loading && <div className="p-8 text-center text-sm text-slate-500">Chargement…</div>}
          {!loading && view === 'day' && (
            <DayView users={filterUser === 'all' ? filteredUsers : filteredUsers.filter(u => u.id === filterUser)} activities={activities} isAdmin={isAdmin}
              onEdit={(a) => { setEditing(a); setShowForm(true); }} onDelete={deleteActivity} />
          )}
          {!loading && view === 'week' && (
            <WeekView start={startOfWeek(current)} activities={activities} isAdmin={isAdmin} onEdit={(a) => { setEditing(a); setShowForm(true); }} />
          )}
          {!loading && view === 'month' && (
            <MonthView current={current} activities={activities} isAdmin={isAdmin}
              onEdit={(a) => { setEditing(a); setShowForm(true); }} onDayClick={(d) => { setCurrent(d); setView('day'); }} />
          )}
        </div>
      </div>

      {showForm && (
        <ActivityForm
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          editing={editing}
          users={users}
          types={types}
          onSaved={() => { setShowForm(false); setEditing(null); fetchActivities(); }}
          defaultDate={fmt(current)}
        />
      )}

      {showAuto && (
        <AutoPlanDialog
          open={showAuto}
          onClose={() => setShowAuto(false)}
          onGenerated={() => { setShowAuto(false); fetchActivities(); }}
          defaultDate={fmt(current)}
        />
      )}
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Vue JOUR — colonnes verticales par employé, missions listées en-dessous
// ═══════════════════════════════════════════════════════════════════
const HOUR_H = 95;
const toMin = (t, d) => { const [h, m] = String(t || d).split(':').map(Number); return (h || 0) * 60 + (m || 0); };

function DayView({ users, activities, isAdmin, onEdit, onDelete }) {
  const byUser = useMemo(() => {
    const m = new Map();
    users.forEach(u => m.set(u.id, []));
    activities.forEach(a => { if (m.has(a.user_id)) m.get(a.user_id).push(a); });
    return m;
  }, [users, activities]);

  if (users.length === 0) {
    return <div className="p-8 text-center text-sm text-slate-500">Aucun utilisateur à afficher. Créez des employés / techniciens depuis le module Utilisateurs.</div>;
  }
  const first = HOURS[0], last = HOURS[HOURS.length - 1];
  const gridH = (last - first + 1) * HOUR_H;
  return (
    <div className="overflow-x-auto" data-testid="pl-day-grid">
      <div className="grid min-w-full" style={{ gridTemplateColumns: `72px repeat(${users.length}, minmax(220px, 1fr))` }}>
        <div className="border-b border-slate-200 h-[70px]" />
        {users.map(u => {
          const n = (byUser.get(u.id) || []).length;
          return (
            <div key={u.id} className="border-b border-l border-slate-200 h-[70px] px-4 flex items-center gap-3" data-testid={`pl-col-${u.id}`}>
              <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-700 font-bold flex items-center justify-center overflow-hidden flex-shrink-0">
                {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : (u.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{u.name}</div>
                <div className="text-sm text-slate-500 truncate">{u.role}{n ? ` · ${n} mission${n > 1 ? 's' : ''}` : ''}</div>
              </div>
            </div>
          );
        })}
        <div className="relative" style={{ height: gridH }}>
          {HOURS.map((h, i) => (
            <div key={h} className="absolute right-3 text-sm text-slate-500 -translate-y-1/2" style={{ top: i * HOUR_H + 2 }}>{String(h).padStart(2, '0')}:00</div>
          ))}
        </div>
        {users.map(u => (
          <div key={u.id} className="relative border-l border-slate-200" style={{ height: gridH }}>
            {HOURS.map((h, i) => <div key={h} className="absolute inset-x-0 border-t border-slate-100" style={{ top: i * HOUR_H }} />)}
            {(byUser.get(u.id) || []).map(a => {
              const s0 = Math.max(toMin(a.heure_debut, '08:00'), first * 60);
              const e0 = Math.max(s0 + 30, toMin(a.heure_fin, `${Math.floor(s0 / 60) + 1}:00`));
              const color = a.type_color || '#3b82f6';
              return (
                <div key={a.id} onClick={() => isAdmin && onEdit(a)} data-testid={`pl-mission-${a.id}`}
                  className={`absolute left-2 right-2 rounded-lg border-l-4 px-3 py-2 overflow-hidden transition-shadow hover:shadow-md ${isAdmin ? 'cursor-pointer' : ''}`}
                  style={{ top: ((s0 - first * 60) / 60) * HOUR_H + 2, height: ((e0 - s0) / 60) * HOUR_H - 4, borderLeftColor: color, background: `${color}1f` }}>
                  {isAdmin && (
                    <button onClick={(e) => { e.stopPropagation(); onDelete(a.id); }} className="absolute top-1.5 right-1.5 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-white/70" title="Supprimer" data-testid={`pl-mission-del-${a.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <div className="text-sm pr-5"><b style={{ color }}>{a.type_label}</b> <span className="text-slate-600">· {a.heure_debut} – {a.heure_fin}</span></div>
                  <div className="font-bold text-slate-900 leading-snug line-clamp-2">{a.titre}</div>
                  {(a.emplacement || a.client) && <div className="text-sm text-slate-500 truncate">{a.emplacement || a.client}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Vue SEMAINE — grille 7 colonnes (jours) × heures
// ═══════════════════════════════════════════════════════════════════
function WeekView({ start, activities, isAdmin, onEdit }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const byDay = useMemo(() => {
    const m = new Map();
    days.forEach(d => m.set(fmt(d), []));
    activities.forEach(a => {
      if (m.has(a.date)) m.get(a.date).push(a);
    });
    return m;
  }, [days, activities]);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        <div className="grid" style={{ gridTemplateColumns: `80px repeat(7, 1fr)` }}>
          <div className="border-r border-b border-slate-200 bg-slate-50 p-2 text-xs font-bold text-slate-500">Heure</div>
          {days.map(d => (
            <div key={fmt(d)} className="border-r border-b border-slate-200 bg-slate-50 p-2 text-center">
              <div className="text-[10px] uppercase font-bold text-slate-500">
                {d.toLocaleDateString('fr-FR', { weekday: 'short' })}
              </div>
              <div className="text-lg font-black text-slate-900">{d.getDate()}</div>
            </div>
          ))}
        </div>
        {HOURS.map(h => (
          <div key={h} className="grid" style={{ gridTemplateColumns: `80px repeat(7, 1fr)` }}>
            <div className="border-r border-b border-slate-100 bg-slate-50 p-2 text-xs text-slate-500">{String(h).padStart(2,'0')}:00</div>
            {days.map(d => {
              const acts = (byDay.get(fmt(d)) || []).filter(a => {
                const hd = parseInt((a.heure_debut || '00:00').split(':')[0], 10);
                return hd === h;
              });
              return (
                <div key={fmt(d)} className="border-r border-b border-slate-100 p-1 min-h-[64px]">
                  {acts.map(a => (
                    <ActivityCard key={a.id} activity={a} isAdmin={isAdmin} onEdit={onEdit} compact />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Vue MOIS — grille 7 × 6
// ═══════════════════════════════════════════════════════════════════
function MonthView({ current, activities, isAdmin, onEdit, onDayClick }) {
  const first = startOfMonth(current);
  const last = endOfMonth(current);
  const startDay = (first.getDay() + 6) % 7;
  const totalCells = Math.ceil((startDay + last.getDate()) / 7) * 7;

  const cells = Array.from({ length: totalCells }, (_, i) => {
    const d = addDays(first, i - startDay);
    return d;
  });

  const byDay = useMemo(() => {
    const m = new Map();
    activities.forEach(a => {
      if (!m.has(a.date)) m.set(a.date, []);
      m.get(a.date).push(a);
    });
    return m;
  }, [activities]);

  return (
    <div>
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => (
          <div key={d} className="p-2 text-center text-xs font-bold text-slate-500 uppercase">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === current.getMonth();
          const acts = byDay.get(fmt(d)) || [];
          const isToday = fmt(d) === fmt(new Date());
          return (
            <div
              key={i}
              onClick={() => onDayClick && onDayClick(d)}
              className={`min-h-[110px] p-1.5 border-r border-b border-slate-100 cursor-pointer hover:bg-blue-50/40 transition ${
                inMonth ? 'bg-white' : 'bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold ${
                  isToday ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center' :
                  inMonth ? 'text-slate-900' : 'text-slate-400'
                }`}>{d.getDate()}</span>
                {acts.length > 0 && <span className="text-[10px] font-bold text-slate-500">{acts.length}</span>}
              </div>
              <div className="space-y-0.5">
                {acts.slice(0, 3).map(a => (
                  <div
                    key={a.id}
                    className="text-[10px] font-semibold rounded px-1.5 py-0.5 truncate text-white cursor-pointer"
                    style={{ backgroundColor: a.type_color || '#3b82f6' }}
                    onClick={(e) => { e.stopPropagation(); if (isAdmin) onEdit(a); }}
                    title={`${a.heure_debut} — ${a.titre} · ${a.user_name}`}
                  >
                    {a.heure_debut} {a.titre}
                  </div>
                ))}
                {acts.length > 3 && <div className="text-[10px] text-slate-500">+{acts.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Carte d'activité (utilisée dans Jour + Semaine)
// ═══════════════════════════════════════════════════════════════════
function ActivityCard({ activity, isAdmin, onEdit, onDelete, compact = false }) {
  const bg = activity.type_color || '#3b82f6';
  return (
    <div
      className="rounded-md text-white p-1.5 mb-1 shadow-sm cursor-pointer hover:shadow-md transition"
      style={{ backgroundColor: bg }}
      onClick={() => isAdmin && onEdit(activity)}
      data-testid={`pl-act-${activity.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className={`font-bold truncate ${compact ? 'text-[10px]' : 'text-xs'}`}>{activity.titre}</div>
          {!compact && (
            <div className="text-[10px] opacity-95 truncate">
              {activity.heure_debut}—{activity.heure_fin}
            </div>
          )}
          {!compact && activity.client && (
            <div className="text-[10px] opacity-90 truncate">{activity.client}</div>
          )}
        </div>
        {isAdmin && !compact && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(activity.id); }}
            className="text-white/70 hover:text-white flex-shrink-0"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const bg = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    orange: 'bg-orange-100 text-orange-700',
    purple: 'bg-purple-100 text-purple-700',
  }[color] || 'bg-slate-100 text-slate-700';
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-lg font-black text-slate-900">{value}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Formulaire de création / édition
// ═══════════════════════════════════════════════════════════════════
function ActivityForm({ open, onClose, editing, users, types, onSaved, defaultDate }) {
  const [form, setForm] = useState({
    user_id: '',
    date: defaultDate || fmt(new Date()),
    heure_debut: '09:00',
    heure_fin: '10:00',
    type: 'tache',
    titre: '',
    description: '',
    client: '',
    emplacement: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        user_id: editing.user_id || '',
        date: editing.date || defaultDate,
        heure_debut: editing.heure_debut || '09:00',
        heure_fin: editing.heure_fin || '10:00',
        type: editing.type || 'tache',
        titre: editing.titre || '',
        description: editing.description || '',
        client: editing.client || '',
        emplacement: editing.emplacement || '',
      });
    }
  }, [editing, defaultDate]);

  const availableTypes = useMemo(() => {
    const targetUser = users.find(u => u.id === form.user_id);
    if (!targetUser) return types;
    if (targetUser.role === 'technicien') return types.filter(t => t.role === 'technicien' || t.role === 'any');
    if (targetUser.role === 'commercial' || targetUser.role === 'employe') return types.filter(t => t.role === 'commercial' || t.role === 'any');
    return types;
  }, [users, types, form.user_id]);

  useEffect(() => {
    if (availableTypes.length && !availableTypes.some(t => t.key === form.type)) {
      setForm(f => ({ ...f, type: availableTypes[0].key }));
    }
  }, [availableTypes, form.type]);

  const submit = async () => {
    if (!form.user_id) return toast.error('Choisissez un employé');
    if (!form.titre) return toast.error('Titre requis');
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.patch(`/planning/${editing.id}`, form);
        toast.success('Activité mise à jour');
      } else {
        await axios.post('/planning', form);
        toast.success('Activité créée');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pl-form">
        <DialogHeader>
          <DialogTitle>{editing ? 'Modifier l\'activité' : 'Nouvelle activité'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-bold text-slate-700">Employé *</label>
            <select
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white mt-1"
              data-testid="pl-form-user"
            >
              <option value="">— Choisir —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-700">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pl-form-date"/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Début</label>
              <Input type="time" value={form.heure_debut} onChange={(e) => setForm({ ...form, heure_debut: e.target.value })} data-testid="pl-form-hstart"/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Fin</label>
              <Input type="time" value={form.heure_fin} onChange={(e) => setForm({ ...form, heure_fin: e.target.value })} data-testid="pl-form-hend"/>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700">Type d'activité *</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white mt-1"
              data-testid="pl-form-type"
            >
              {availableTypes.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700">Titre *</label>
            <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex : Rendez-vous BPMC" data-testid="pl-form-title"/>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-700">Client</label>
              <Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Nom du client" data-testid="pl-form-client"/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Emplacement / Panneau</label>
              <Input value={form.emplacement} onChange={(e) => setForm({ ...form, emplacement: e.target.value })} placeholder="Ex : PK0, Hôtel Levis" data-testid="pl-form-emp"/>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700">Description</label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes..." data-testid="pl-form-desc"/>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="pl-form-save">
            {saving ? 'Enregistrement…' : (editing ? 'Enregistrer' : 'Créer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Dialog Planning Automatique — génère 7 jours de tournées en un clic
// ═══════════════════════════════════════════════════════════════════
function AutoPlanDialog({ open, onClose, onGenerated, defaultDate }) {
  const today = defaultDate || fmt(new Date());
  const oneWeek = fmt(addDays(new Date(today), 6));
  const [form, setForm] = useState({
    date_from: today,
    date_to: oneWeek,
    include_technicien: true,
    include_commercial: true,
    include_employe: true,
    skip_weekends: true,
    replace_existing: true,
    recurring: false,
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [tpl, setTpl] = useState(null);

  useEffect(() => {
    if (open) {
      axios.get('/planning/recurring-template').then(r => setTpl(r.data)).catch(() => setTpl(null));
    }
  }, [open]);

  const submit = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await axios.post('/planning/auto-generate', form);
      setResult(r.data);
      toast.success(r.data?.message || 'Planning auto généré');
      setTimeout(() => { onGenerated(); }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur planning auto');
    } finally { setRunning(false); }
  };

  const applyPreset = (nbDays) => {
    setForm(f => ({
      ...f,
      date_from: today,
      date_to: fmt(addDays(new Date(today), nbDays - 1)),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="pl-auto-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Planning automatique
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100 p-3 text-sm text-slate-700">
            <div className="font-bold text-purple-900 mb-1">Ce que ça génère :</div>
            <ul className="space-y-0.5 text-xs">
              <li><b>Techniciens</b> — 2 interventions/jour rotation sur les 14 emplacements</li>
              <li><b>Commerciaux</b> — 1 prospection + 1 relance + 1 RDV client/jour</li>
              <li><b>Employés</b> — 1 tâche administrative/jour</li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-bold text-slate-700 mb-2">Période à planifier</div>
            <div className="flex gap-2 mb-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => applyPreset(3)} data-testid="pl-auto-preset-3">3 jours</Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset(7)} data-testid="pl-auto-preset-7" className="border-purple-300 text-purple-700">7 jours (recommandé)</Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset(14)} data-testid="pl-auto-preset-14">14 jours</Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset(30)} data-testid="pl-auto-preset-30">30 jours</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Du</label>
                <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="pl-auto-from"/>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Au</label>
                <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="pl-auto-to"/>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold text-slate-700 mb-2">Rôles à inclure</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'include_technicien', label: 'Techniciens' },
                { key: 'include_commercial', label: 'Commerciaux' },
                { key: 'include_employe',    label: 'Employés' },
              ].map(r => (
                <label key={r.key} className={`flex items-center gap-2 border-2 rounded-lg p-2.5 cursor-pointer ${
                  form[r.key] ? 'border-purple-400 bg-purple-50' : 'border-slate-200 bg-white'
                }`}>
                  <input type="checkbox" checked={form[r.key]} onChange={(e) => setForm({ ...form, [r.key]: e.target.checked })} data-testid={`pl-auto-${r.key}`}/>
                  <span className="text-sm font-semibold text-slate-800">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs flex-wrap">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={form.skip_weekends} onChange={(e) => setForm({ ...form, skip_weekends: e.target.checked })} data-testid="pl-auto-skip"/>
              <span>Ignorer les week-ends</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={form.replace_existing} onChange={(e) => setForm({ ...form, replace_existing: e.target.checked })} data-testid="pl-auto-replace"/>
              <span>Remplacer les activités auto existantes</span>
            </label>
          </div>

          {/* Récurrence hebdomadaire */}
          <label className="flex items-center gap-2 border-2 border-purple-200 rounded-lg p-3 bg-gradient-to-r from-purple-50 to-white cursor-pointer">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
              data-testid="pl-auto-recurring"
              className="w-4 h-4"
            />
            <div className="flex-1">
              <div className="text-sm font-bold text-purple-900">Récurrence hebdomadaire</div>
              <div className="text-[11px] text-slate-600">Le planning se régénérera automatiquement chaque lundi à 00:15 UTC pour la semaine à venir.</div>
              {tpl?.enabled && (
                <div className="mt-1 text-[10px] font-bold text-emerald-700">✓ Récurrence actuellement active</div>
              )}
            </div>
          </label>

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <div className="font-bold text-emerald-900">{result.message}</div>
              <div className="text-xs text-emerald-700 mt-1">
                Techniciens : {result.stats?.technicien || 0} · Commerciaux : {result.stats?.commercial || 0} · Employés : {result.stats?.employe || 0}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={running}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={running}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white gap-2"
            data-testid="pl-auto-generate"
          >
            {running ? <><Loader2 className="h-4 w-4 animate-spin"/> Génération…</> : <><Sparkles className="h-4 w-4"/> Générer maintenant</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
